import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type Contacto } from "@/lib/api";
import { useToast } from "@/ui/Toast";

type Props = {
  clienteId: number;
  contactoId: number | null;
  conQuien: string;
  onChange: (next: { contactoId: number | null; conQuien: string }) => void;
  // Si el caso ya tiene un contacto guardado (contacto_nombre/email/
  // telefono del Caso, no de la tabla contactos), lo usamos para prefillear
  // el form de "+ Nuevo" — registrar al cliente con un click.
  prefillFromCaso?: {
    nombre: string | null;
    email: string | null;
    telefono: string | null;
  };
};

/** Normaliza un nombre para comparar: sin acentos, lowercase, sin espacios
 * extra. Usado para dedup heuristico cuando un correo del formulario crea
 * caso tras caso para el mismo cliente — antes se generaban contactos
 * duplicados; ahora detectamos match por nombre y proponemos reusar el
 * existente. */
export function normalizeName(s: string): string {
  if (!s) return "";
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Picker de contacto del cliente. Eliges uno de los registrados o
 * registras uno nuevo inline. Mantiene en el componente padre tanto el
 * FK (contactoId) como el nombre como snapshot (conQuien).
 *
 * Dedup: al registrar, primero busca match exacto por nombre normalizado
 * contra los contactos cargados; si lo encuentra usa ese en lugar de
 * crear un duplicado.
 */
export default function ContactoPicker({
  clienteId,
  contactoId,
  conQuien,
  onChange,
  prefillFromCaso,
}: Props) {
  const toast = useToast();
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(false);
  const [regNombre, setRegNombre] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regTelefono, setRegTelefono] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const list = await api.get<Contacto[]>(
        `/api/clientes/${clienteId}/contactos`,
      );
      setContactos(list);
    } catch (err) {
      // si falla la carga, dejamos lista vacia — el user puede registrar
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar contactos", msg);
      setContactos([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // Auto-seleccionar el contacto existente que matchee el prefill (email o
  // nombre) de la IA/caso, para no obligar a re-elegirlo. Una sola vez.
  const prefillAplicado = useRef(false);
  useEffect(() => {
    if (prefillAplicado.current || contactoId !== null || contactos.length === 0)
      return;
    const email = prefillFromCaso?.email?.trim().toLowerCase();
    const nombre = prefillFromCaso?.nombre?.trim().toLowerCase();
    if (!email && !nombre) return;
    const match = contactos.find(
      (c) =>
        (!!email && (c.email || "").toLowerCase() === email) ||
        (!!nombre && c.nombre.toLowerCase() === nombre),
    );
    if (match) {
      prefillAplicado.current = true;
      onChange({ contactoId: match.id, conQuien: match.nombre });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactos, prefillFromCaso]);

  // Sugerencias mientras el user escribe en "+ Nuevo": contactos del
  // cliente cuyo nombre normalizado empieza con o contiene el texto.
  // Limitadas a 3 para no inundar el UI.
  const sugerencias = useMemo(() => {
    const needle = normalizeName(regNombre);
    if (!needle || needle.length < 2) return [];
    return contactos
      .filter((c) => {
        const n = normalizeName(c.nombre);
        return n === needle || n.includes(needle) || needle.includes(n);
      })
      .slice(0, 3);
  }, [contactos, regNombre]);

  function pick(idStr: string) {
    if (!idStr) {
      onChange({ contactoId: null, conQuien: "" });
      return;
    }
    const id = Number(idStr);
    const c = contactos.find((x) => x.id === id);
    if (c) onChange({ contactoId: c.id, conQuien: c.nombre });
  }

  function elegirExistente(c: Contacto) {
    onChange({ contactoId: c.id, conQuien: c.nombre });
    cancelarReg();
    toast.success("Contacto seleccionado", c.nombre);
  }

  function cancelarReg() {
    setRegOpen(false);
    setRegNombre("");
    setRegEmail("");
    setRegTelefono("");
  }

  async function registrar() {
    if (!regNombre.trim()) return;
    // Dedup: si ya existe un contacto con nombre normalizado identico,
    // lo reusamos en lugar de crear duplicado. Esto evita la avalancha
    // de duplicados cuando el modulo de "Crear caso desde correo de
    // formulario" se ejecuta para el mismo cliente repetidas veces.
    const needle = normalizeName(regNombre);
    const exacto = contactos.find((c) => normalizeName(c.nombre) === needle);
    if (exacto) {
      elegirExistente(exacto);
      return;
    }
    setSaving(true);
    try {
      const c = await api.post<Contacto>(
        `/api/clientes/${clienteId}/contactos`,
        {
          nombre: regNombre.trim(),
          email: regEmail.trim() || null,
          telefono: regTelefono.trim() || null,
        },
      );
      setContactos((p) =>
        [...p, c].sort((a, b) => a.nombre.localeCompare(b.nombre)),
      );
      onChange({ contactoId: c.id, conQuien: c.nombre });
      cancelarReg();
      toast.success("Contacto registrado", c.nombre);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude registrar", msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <select
          value={contactoId ?? ""}
          onChange={(e) => pick(e.target.value)}
          className="input flex-1 min-w-0 text-sm"
          disabled={loading || regOpen}
        >
          <option value="">
            {loading ? "Cargando…" : "— elige un contacto —"}
          </option>
          {contactos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
              {c.email ? ` (${c.email})` : ""}
            </option>
          ))}
        </select>
        {!regOpen && (
          <button
            type="button"
            onClick={() => {
              // Prefill con el contacto del caso si existe; si no, con lo
              // que el user haya tipeado antes (raro).
              const pf = prefillFromCaso;
              setRegNombre((pf?.nombre || conQuien || "").trim());
              setRegEmail((pf?.email || "").trim());
              setRegTelefono((pf?.telefono || "").trim());
              setRegOpen(true);
            }}
            className="btn-ghost text-sm whitespace-nowrap"
          >
            + Nuevo
          </button>
        )}
      </div>

      {!loading && !regOpen && contactos.length === 0 && prefillFromCaso?.nombre && (
        <p className="text-xs text-zinc-500">
          Este caso tiene a <strong>{prefillFromCaso.nombre}</strong> como
          contacto. Pulsa <span className="text-accent-400">+ Nuevo</span>{" "}
          para registrarlo en el cliente.
        </p>
      )}

      {regOpen && (
        <div className="rounded border border-ink-700 bg-ink-950/40 p-3 space-y-2">
          <p className="text-xs text-zinc-500">Registrar un contacto nuevo</p>
          <input
            type="text"
            placeholder="Nombre *"
            value={regNombre}
            onChange={(e) => setRegNombre(e.target.value)}
            className="input w-full text-sm"
            autoFocus
          />
          {sugerencias.length > 0 && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-2 space-y-1">
              <p className="text-[11px] text-amber-200">
                {sugerencias.length === 1
                  ? "Ya existe un contacto similar — ¿es el mismo?"
                  : "Ya existen contactos similares — ¿alguno es el mismo?"}
              </p>
              <div className="flex flex-wrap gap-1">
                {sugerencias.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => elegirExistente(c)}
                    className="text-xs px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20 transition"
                  >
                    {c.nombre}
                    {c.email && (
                      <span className="text-amber-300/70 ml-1">
                        · {c.email}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="email"
              placeholder="Correo (opcional)"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
              className="input w-full text-sm"
            />
            <input
              type="text"
              placeholder="Teléfono (opcional)"
              value={regTelefono}
              onChange={(e) => setRegTelefono(e.target.value)}
              className="input w-full text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={registrar}
              disabled={saving || !regNombre.trim()}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saving ? "Guardando…" : "Registrar y elegir"}
            </button>
            <button
              type="button"
              onClick={cancelarReg}
              className="btn-ghost text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
